import {
  PVP_HEALTH_MILLI_PER_UNIT,
  type PvpParticipantId,
  type PvpResult,
  type PvpRuleset,
  type PvpSeat,
  type PvpTeam,
  pvpParseParticipantId,
  pvpParseTeam,
  pvpParticipantId,
  pvpRefToSeat,
} from '@kenocyber/shared';
import type { SimulationRngState } from '../simulation-rng';
import type { PvpRuneKind } from './balance';
import { PVP_MILLI_TICKS_PER_TICK, pvpIsDefaultCombatProfile } from './combat-profile';
import type { PvpParticipantEvent, PvpParticipantShipState, PvpTeamResult } from './roster';
import type { PvpRuneState, PvpShipEffects } from './runes';

/**
 * The 1x1's border post: everything that is still spelled with a SEAT.
 *
 * ## Why the seats did not simply go away
 *
 * `PvpSeat` is the shape the wire schemas, the `pvp_matches` row, the signed
 * ticket and every persisted room snapshot already speak. Rewriting all four in
 * the same commit that generalises the simulation is how a working two-player
 * duel becomes a broken six-player one - so the simulation is generalised and
 * the seats are kept, with the correspondence written down HERE and refused
 * everywhere it would be a lie.
 *
 * ## The whole correspondence, and its edge
 *
 * Seat `a` is participant `a1`; seat `b` is participant `b1`. There is no third
 * sentence: `a2`, `a3`, `b2` and `b3` have no seat, and every function below
 * that would have to invent one THROWS instead. That refusal is the load-bearing
 * part of this file - a `winner_seat` of `'a'` written for a team match, or a
 * `hit` frame addressed to seat `a` because `a2` was on team `a`, is a wrong
 * answer that nothing downstream could detect.
 *
 * ## What is temporary, and what is not
 *
 * The 3x3 REFUSALS are temporary: the next commit adds a snapshot v3 and a
 * participant-addressed frame, and these functions stop being the only way out
 * of the simulation. The 1x1 adapters are not - a duel will go on being played
 * over a two-seat wire for as long as those rows exist.
 */

/* -------------------------------------------------------------------------- */
/*  The legacy shapes, unchanged                                              */
/* -------------------------------------------------------------------------- */

/**
 * One ship as the persisted duel snapshot has always recorded it.
 *
 * Byte-for-byte the structure this file's git history shows, `seat` included.
 * It is a PERSISTENCE format: `PvpRoomPersistedState` embeds it, rooms in
 * Durable Object storage are written in it, and a build that changed a field
 * here would resume somebody's match as a different one.
 */
export interface PvpShipState {
  readonly seat: PvpSeat;
  x: number;
  y: number;
  aimX: number;
  aimY: number;
  health: number;
  cooldown: number;
  moveX: number;
  moveY: number;
  firing: boolean;
  connected: boolean;
  appliedSeq: number;
  effects: PvpShipEffects;
  teleportTo: { x: number; y: number } | null;
}

/** One shot as the persisted duel snapshot records it. */
export interface PvpProjectileState {
  readonly id: number;
  readonly seat: PvpSeat;
  readonly damage: number;
  x: number;
  y: number;
  readonly vx: number;
  readonly vy: number;
  life: number;
}

/**
 * The duel snapshot, exactly as it was.
 *
 * A pair of ships, and that is not an oversight - it is the format on disk. A
 * 3x3 does not have one yet and must not be squeezed into this one: two of six
 * ships is not a smaller snapshot, it is a wrong one. See
 * `snapshot_not_supported_for_ruleset`.
 */
export interface PvpSnapshot {
  readonly tick: number;
  readonly ships: readonly [PvpShipState, PvpShipState];
  readonly projectiles: readonly PvpProjectileState[];
  readonly nextProjectileId: number;
  readonly result: PvpResult | null;
  readonly runes: readonly PvpRuneState[];
  readonly rng: SimulationRngState;
}

/** A presentation event addressed to a seat, as the 1x1 path expects it. */
export type PvpEvent =
  | {
      readonly kind: 'hit';
      readonly seat: PvpSeat;
      readonly x: number;
      readonly y: number;
      readonly amount: bigint;
    }
  | { readonly kind: 'fired'; readonly seat: PvpSeat; readonly x: number; readonly y: number }
  | { readonly kind: 'death'; readonly seat: PvpSeat; readonly x: number; readonly y: number }
  | {
      readonly kind: 'rune';
      readonly seat: PvpSeat;
      readonly rune: PvpRuneKind;
      readonly x: number;
      readonly y: number;
    }
  | { readonly kind: 'teleport'; readonly seat: PvpSeat; readonly x: number; readonly y: number };

/* -------------------------------------------------------------------------- */
/*  Refusals                                                                  */
/* -------------------------------------------------------------------------- */

/** The ruleset whose participants have seats. There is exactly one. */
export const PVP_DUEL_RULESET: PvpRuleset = 'duel_1v1';

/**
 * Why the two-seat boundary said no.
 *
 * Machine codes, in the style of every other refusal in this repository: a
 * caller keys on them, and `snapshot_not_supported_for_ruleset` in particular is
 * the named, temporary limitation this round leaves behind.
 */
export type PvpLegacyRefusal =
  /** A 3x3 has no persisted format yet. It is not silently reduced to two ships. */
  | 'snapshot_not_supported_for_ruleset'
  /** A seat-shaped answer was asked of a match whose participants have no seats. */
  | 'seat_not_supported_for_ruleset'
  /** This particular participant has no seat - slot 2 and slot 3 never do. */
  | 'seat_not_supported_for_participant'
  /**
   * The historic format has no field for a hull that is not the shipped one.
   *
   * A duel whose ships carry a non-default `PvpCombatProfile` cannot be
   * written to the v2 snapshot without dropping it, and dropping it would
   * resume the match with everybody's damage, health, speed and cadence quietly
   * reset. So it refuses instead, and the gate that first grants a profile is
   * the gate that has to move duels onto the v3 format.
   *
   * Alcançável desde o gate V28F.B: a nave equipada leva a sua contagem de
   * Corações Primordiais para o perfil, e qualquer contagem diferente de zero
   * torna o perfil não-padrão. O V28E já tinha movido a persistência para o
   * formato v3 exatamente por isto, portanto a recusa protege o que resta - o
   * caminho de leitura legado e quem o voltar a ligar.
   */
  | 'duel_snapshot_cannot_carry_a_profile';

export class PvpLegacyDuelError extends RangeError {
  override readonly name = 'PvpLegacyDuelError';

  constructor(
    readonly code: PvpLegacyRefusal,
    message: string,
  ) {
    super(message);
  }
}

/**
 * Refuses anything that is not the shipped duel.
 *
 * Called at the top of every seat-shaped accessor rather than once at
 * construction, because the mistake being prevented is a 3x3 reaching a 1x1
 * path - and that reach happens at the call, not at the build.
 */
export function pvpRequireDuelRuleset(ruleset: PvpRuleset, code: PvpLegacyRefusal): void {
  if (ruleset !== PVP_DUEL_RULESET) {
    throw new PvpLegacyDuelError(code, `${code}: ${ruleset}`);
  }
}

/* -------------------------------------------------------------------------- */
/*  Identity, both ways                                                       */
/* -------------------------------------------------------------------------- */

/** A wire seat as a team. `a` and `b` are the same letters and not the same type. */
export function pvpDuelSeatToTeam(seat: PvpSeat): PvpTeam {
  const team = pvpParseTeam(seat);
  if (team === null) {
    // Unreachable while `PvpSeat` is `'a' | 'b'`; asserted so widening the seat
    // union produces a failure here rather than a silent `undefined` team.
    throw new PvpLegacyDuelError('seat_not_supported_for_participant', `not a team: ${seat}`);
  }
  return team;
}

/** Wire seat `a` is participant `a1`; wire seat `b` is participant `b1`. */
export function pvpDuelSeatToParticipantId(seat: PvpSeat): PvpParticipantId {
  return pvpParticipantId(pvpDuelSeatToTeam(seat), 1);
}

/**
 * A participant back to a wire seat, or a refusal.
 *
 * `a1` and `b1` only. `a2` is parsed against the DUEL ruleset, which has no
 * slot 2 at all, so it fails to parse and throws - the same answer
 * `pvpRefToSeat` gives, reached one step earlier.
 */
export function pvpParticipantIdToDuelSeat(participantId: PvpParticipantId): PvpSeat {
  const ref = pvpParseParticipantId(participantId, PVP_DUEL_RULESET);
  if (ref === null) {
    throw new PvpLegacyDuelError(
      'seat_not_supported_for_participant',
      `participant ${participantId} has no 1x1 seat`,
    );
  }
  const seat = pvpRefToSeat(ref);
  if (seat === null) {
    throw new PvpLegacyDuelError(
      'seat_not_supported_for_participant',
      `participant ${participantId} has no 1x1 seat`,
    );
  }
  return seat;
}

/**
 * A team back to a wire seat, in a duel and nowhere else.
 *
 * Routed through `pvpRefToSeat` rather than asserting the letter, so the one
 * place that decides "slot 1 has a seat and no other slot does" stays one place.
 */
export function pvpTeamToDuelSeat(team: PvpTeam): PvpSeat {
  const seat = pvpRefToSeat({ team, slot: 1 });
  if (seat === null) {
    throw new PvpLegacyDuelError('seat_not_supported_for_ruleset', `team ${team} has no seat`);
  }
  return seat;
}

/* -------------------------------------------------------------------------- */
/*  Values, one way only                                                      */
/* -------------------------------------------------------------------------- */

/**
 * A canonical event as the 1x1 path expects it.
 *
 * The seat comes from the PARTICIPANT and never from the team. A `hit` on `a2`
 * belongs to team `a` and to no seat at all, and answering `'a'` because the
 * team letter matched would address the frame to the wrong ship - which is the
 * exact mistake this function exists to make impossible.
 */
export function pvpParticipantEventToDuelEvent(event: PvpParticipantEvent): PvpEvent | null {
  const seat = pvpParticipantIdToDuelSeat(event.participantId);
  switch (event.kind) {
    case 'hit':
      return { kind: 'hit', seat, x: event.x, y: event.y, amount: event.amount };
    case 'fired':
      return { kind: 'fired', seat, x: event.x, y: event.y };
    case 'death':
      return { kind: 'death', seat, x: event.x, y: event.y };
    case 'rune':
      return { kind: 'rune', seat, rune: event.rune, x: event.x, y: event.y };
    case 'teleport':
      return { kind: 'teleport', seat, x: event.x, y: event.y };
    case 'proc':
      /*
       * O formato de duelo HISTÓRICO não tem forma para um proc de joia, e
       * dar-lhe uma seria mudar um formato que descreve partidas já jogadas.
       *
       * `null` diz "este acontecimento não atravessa para aqui", e o chamador
       * deixa-o cair. Nenhuma partida antiga perde nada: nenhuma delas teve
       * procs, e nenhum cliente de duelo os desenha.
       */
      return null;
    case 'blast':
      /*
       * E uma explosão também não atravessa, pela mesma razão exata.
       *
       * O formato histórico tem cinco tipos de evento e nenhum deles é um
       * círculo com raio. Alargá-lo agora mudaria um formato que descreve
       * partidas já jogadas, para lhes acrescentar um acontecimento que nenhuma
       * delas teve.
       */
      return null;
    case 'bleed':
      /*
       * E uma detonação de sangramento também não, pela mesma razão exata.
       *
       * O formato histórico tem cinco tipos de evento e nenhum deles descreve
       * uma ordem do jogador a rebentar marcas em vários alvos ao mesmo tempo.
       */
      return null;
    case 'reflect':
      /*
       * E uma reflexão também não, pela mesma razão exata.
       *
       * O formato histórico tem cinco tipos de evento e nenhum deles descreve um
       * payload a mudar de dono entre dois cascos. Alargá-lo agora mudaria um
       * formato que descreve partidas já jogadas, para lhes acrescentar um
       * acontecimento que nenhuma delas teve.
       */
      return null;
  }
}

/**
 * A ship as the persisted duel snapshot records it.
 *
 * `effects` and `teleportTo` are carried by REFERENCE, which is what
 * the two-seat snapshot always did (`{ ...ship }` is a shallow copy). Deep
 * copying here would be a different simulation, not a tidier one, and this
 * round changes no 1x1 behaviour at all - see `duel-invariance.test.ts`.
 */
export function pvpShipToDuelShip(ship: PvpParticipantShipState): PvpShipState {
  /*
   * Two refusals rather than two silent conversions.
   *
   * The historic shape has no profile field and counts the cooldown in whole
   * TICKS. Both are exactly right for every duel that exists, and both stop
   * being right the moment a jewel changes a hull - so the encoder says so out
   * loud instead of writing a record that resumes a different match.
   */
  if (!pvpIsDefaultCombatProfile(ship.profile)) {
    throw new PvpLegacyDuelError(
      'duel_snapshot_cannot_carry_a_profile',
      `${ship.participantId} carries a custom combat profile; the v2 duel snapshot has no field for it`,
    );
  }
  if (ship.cooldownMilliTicks % PVP_MILLI_TICKS_PER_TICK !== 0) {
    throw new PvpLegacyDuelError(
      'duel_snapshot_cannot_carry_a_profile',
      `${ship.participantId} is ${ship.cooldownMilliTicks} milli-ticks from firing, which is not a whole tick`,
    );
  }
  /*
   * A terceira, pela mesma razão exata (gate V28F.B).
   *
   * A vida fracionária é estado que decide o futuro: um casco com 359 600 milli
   * morre um tiro mais cedo do que um com 359 000. O formato v2 guarda PONTOS
   * INTEIROS e não tem onde pôr a fração, portanto um casco que a carregue é
   * recusado em vez de ser truncado (gate V28G.S.1).
   *
   * Nenhum duelo existente chega aqui: um casco por omissão tem 1 000 000 milli
   * e um tiro sem corações remove um múltiplo exato de mil.
   */
  if (ship.healthMilli % PVP_HEALTH_MILLI_PER_UNIT !== 0n) {
    throw new PvpLegacyDuelError(
      'duel_snapshot_cannot_carry_a_profile',
      `${ship.participantId} holds ${ship.healthMilli} milli of health; the v2 duel snapshot stores whole points`,
    );
  }
  return {
    seat: pvpParticipantIdToDuelSeat(ship.participantId),
    x: ship.x,
    y: ship.y,
    aimX: ship.aimX,
    aimY: ship.aimY,
    health: Number(ship.healthMilli / PVP_HEALTH_MILLI_PER_UNIT),
    cooldown: ship.cooldownMilliTicks / PVP_MILLI_TICKS_PER_TICK,
    moveX: ship.moveX,
    moveY: ship.moveY,
    firing: ship.firing,
    connected: ship.connected,
    appliedSeq: ship.appliedSeq,
    effects: ship.effects,
    teleportTo: ship.teleportTo,
    /*
     * `detonateBleed` NÃO tem campo aqui, e não há quarta recusa (gate V28G.N).
     *
     * As outras três recusam porque o que falta ao formato v2 decide o futuro:
     * um perfil, meio tique de cadência, um resto de dano por pagar. Uma ordem
     * de detonar não decide nada num casco que este formato saiba escrever - ele
     * tem o perfil PADRÃO, portanto não tem joias, portanto não tem marca
     * nenhuma para rebentar, e a ordem seria um `no-op` de qualquer maneira.
     *
     * Recusá-la seria recusar uma sala v2 legítima por causa de um botão que
     * nada faz nela; escrevê-la exigiria alargar um formato que descreve
     * partidas já jogadas.
     */
  };
}

/**
 * A team result as the two-seat wire and the `pvp_matches` row record it.
 *
 * A winning team becomes its slot-1 seat, which in a duel is the same person -
 * the same fact `winner_seat` has always stored. A draw and a service failure
 * are `null` here exactly as before, told apart by `reason`.
 */
export function pvpTeamResultToDuelResult(result: PvpTeamResult): PvpResult {
  return {
    winner: result.winner === null ? null : pvpTeamToDuelSeat(result.winner),
    reason: result.reason,
    ticks: result.ticks,
    /*
     * The abandoning PARTICIPANT becomes their seat, through the same refusal
     * every other conversion here goes through. In a duel `a1` and `b1` are the
     * only chairs there are, so this is total; a team result reaching it throws
     * rather than calling `a2` "seat a", which is a wrong answer that nothing
     * downstream could detect.
     */
    abandonment:
      result.abandonment === null
        ? null
        : {
            cause: result.abandonment.cause,
            seat: pvpParticipantIdToDuelSeat(result.abandonment.participantId),
          },
  };
}
